/**
 * `gamification-core` — projection of minimized telemetry to GameEvents.
 *
 * Gamification is DERIVED from `usage_event` (category + active seconds —
 * NEVER app/host/title/content): it adds no tables and does not mutate the oplog.
 * `applyEvents` is pure and deterministic → the same telemetry produces the same
 * `PlayerState` (local on client; recomputable on server for anti-cheat).
 *
 * Only PRODUCTIVE activity adds; distracting/neutral NEVER subtracts (not
 * punitive). A "focus block" is a productive stretch above the threshold
 * (`MIN_FOCUS_SECS`). It is classified with the profile's productivity catalog
 * (default: `baselineCatalog`), just like the dashboard — the category, never the
 * content.
 */
import { baselineCatalog, isActivityCategory, type ProductivityCatalog } from "../workforce/types";
import { JORNADA_EVENTS, jornadaGamificationConfig, MIN_FOCUS_SECS } from "./config";
import { applyEvents, initialState } from "./engine";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/**
 * Minimal `usage_event` row needed for the projection. ONLY category +
 * active seconds + start metadata; never app/host/title (minimization §2e).
 */
export interface UsageEventRow {
  id: string | null;
  category: string | null;
  active_secs: number | null;
  started_at: string | null;
}

/** Is the category classified as productive with the given catalog? */
function isProductiveCategory(category: string | null, catalog: ProductivityCatalog): boolean {
  const cls =
    (isActivityCategory(category) ? catalog.category_defaults[category] : undefined) ??
    catalog.fallback;
  return cls === "productive";
}

/**
 * Translates minimized telemetry into gamification events (ascending order
 * by time, as required by the streak reducer). Short, non-productive rows or
 * rows without a valid timestamp are ignored.
 */
export function deriveGameEvents(
  rows: readonly UsageEventRow[],
  catalog: ProductivityCatalog = baselineCatalog(),
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    if ((row.active_secs ?? 0) < MIN_FOCUS_SECS) {
      continue;
    }
    if (!isProductiveCategory(row.category, catalog)) {
      continue;
    }
    const raw = row.started_at;
    if (!raw) {
      continue;
    }
    const at = Date.parse(raw);
    if (Number.isNaN(at)) {
      continue;
    }
    events.push({
      type: JORNADA_EVENTS.focusBlock,
      at,
      day: dayKeyUTC(at),
      meta: { usageId: row.id },
    });
  }
  // The feed may arrive started_at DESC; the reducer needs ASC chronological order.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from telemetry. Pure: same telemetry → same
 * state. Returns XP/level/streaks/badges/quests ready for the UI.
 */
export function deriveGameState(
  rows: readonly UsageEventRow[],
  catalog?: ProductivityCatalog,
): PlayerState {
  const events = deriveGameEvents(rows, catalog);
  return applyEvents(initialState(), events, jornadaGamificationConfig).state;
}

/**
 * OPT-IN Gating: if the worker has NOT enabled gamification (default
 * OFF), the state is initial (0 XP, no streaks/badges) — nothing is computed or
 * displayed. Pure logic to allow testing the gating without the DOM.
 */
export function deriveGameStateIfEnabled(
  enabled: boolean,
  rows: readonly UsageEventRow[],
  catalog?: ProductivityCatalog,
): PlayerState {
  return enabled ? deriveGameState(rows, catalog) : initialState();
}
